import React from 'react';
import { Card, Flex, Typography } from 'antd';
import { formatPriceIndian } from '../../constants/functions';

const { Title, Text } = Typography;

interface SummaryItem {
    label: string;
    value: number | string;
    formatAsCurrency?: boolean;
}

interface InvoiceSummaryProps {
    summary: {
        items?: SummaryItem[];
        [key: string]: any; // Allow any other properties for backward compatibility
    } | null;
}

const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({ summary }) => {
    if (!summary) return null;

    // If summary has items array, use that (new flexible format)
    if (summary.items && Array.isArray(summary.items)) {
        return (
            <Flex gap={12} wrap="wrap" style={{ width: '100%', marginBottom: 8 }}>
                {summary.items.map((item: SummaryItem, index: number) => (
                    <Card
                        key={index}
                        size="small"
                        style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #ff4d4f' }}
                    >
                        <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                            <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                {item.label}:
                            </Text>
                            <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                {item.formatAsCurrency
                                    ? `₹${formatPriceIndian(typeof item.value === 'number' ? item.value : parseFloat(item.value) || 0)}`
                                    : item.value || 0
                                }
                            </Title>
                        </Flex>
                    </Card>
                ))}
            </Flex>
        );
    }

    // Backward compatibility: If no items array, try to build from legacy format
    const legacyItems: SummaryItem[] = [];

    if (summary.totalInvoices !== undefined) {
        legacyItems.push({ label: 'Total Invoices', value: summary.totalInvoices });
    }
    if (summary.totalAmount !== undefined) {
        legacyItems.push({ label: 'Total Amount', value: summary.totalAmount, formatAsCurrency: true });
    }
    if (summary.totalGST !== undefined) {
        legacyItems.push({ label: 'Total GST', value: summary.totalGST, formatAsCurrency: true });
    }
    if (summary.totalTDS !== undefined) {
        legacyItems.push({ label: 'Total TDS', value: summary.totalTDS, formatAsCurrency: true });
    }

    if (legacyItems.length === 0) return null;

    return (
        <Flex gap={12} wrap="wrap" style={{ width: '100%', marginBottom: 8 }}>
            {legacyItems.map((item: SummaryItem, index: number) => (
                <Card
                    key={index}
                    size="small"
                    style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #ff4d4f' }}
                >
                    <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                        <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                            {item.label}:
                        </Text>
                        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                            {item.formatAsCurrency
                                ? `₹${formatPriceIndian(typeof item.value === 'number' ? item.value : parseFloat(item.value) || 0)}`
                                : item.value || 0
                            }
                        </Title>
                    </Flex>
                </Card>
            ))}
        </Flex>
    );
};

export default InvoiceSummary;
